<template>
  <header class="app-header">
    <h1>Telestrations</h1>
    <!-- Nothing in here is usable from behind the gate, and a stale
         saved game in localStorage was enough to show a locked-out
         visitor a Leave button. The title stays; the controls go. -->
    <div v-if="isApproved" class="app-header__right">
      <AdminPanel v-if="isAdmin" />
      <button
        class="icon-button"
        title="How to play"
        aria-label="How to play"
        @click="showHowTo = true"
      >?</button>
      <span v-if="roomMeta?.roomCode && !inLobby" class="header-code">{{ roomMeta.roomCode }}</span>
      <button v-if="roomId" class="icon-button" title="Leave" aria-label="Leave" @click="confirmLeave">✕</button>
    </div>
  </header>

  <main class="app-main">
    <SiteGate v-if="!isApproved" />
    <RevealScreen v-else-if="isRevealing" @leave="leave" />
    <PlayScreen v-else-if="playing" />
    <RoomLobby v-else-if="roomId || wantsOnline" @back="wantsOnline = false" />
    <div v-else class="screen">
      <h2>Telestrations</h2>
      <p class="muted">
        The drawing telephone game. Write a phrase, draw what you're handed,
        guess what you're handed, and see what survives.
      </p>
      <p class="muted">
        Needs at least three players. Short of people? Robots can fill the
        empty seats — they draw and guess for themselves, badly.
      </p>
      <button class="primary" @click="wantsOnline = true">Start or join a game</button>

      <!-- The way home. Matt: "From the game lobby, I need a way to get back
           to... the main lobby." Same mark and wording in every game; the
           icon is the hub's own ring-of-seats logo. -->
      <a class="hub-link" href="https://aroundtableround.com/" title="All the games">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.4" opacity="0.5" />
          <circle cx="20" cy="12" r="1.7" fill="currentColor" /><circle cx="17.66" cy="17.66" r="1.7" fill="currentColor" />
          <circle cx="12" cy="20" r="1.7" fill="currentColor" /><circle cx="6.34" cy="17.66" r="1.7" fill="currentColor" />
          <circle cx="4" cy="12" r="1.7" fill="currentColor" /><circle cx="6.34" cy="6.34" r="1.7" fill="currentColor" />
          <circle cx="12" cy="4" r="1.7" fill="currentColor" /><circle cx="17.66" cy="6.34" r="1.7" fill="currentColor" />
        </svg>
        Around Table Round
      </a>

      <!-- The install offer lives HERE, on the menu, and nowhere else.
           Matt: "the install button should only appear on the hub for each
           game... make sure it's not interfering with anything else. It's not
           a main use case." It used to be mounted in App.vue and fixed to the
           top-right corner of every screen, mid-game included. -->
      <MyStatsPanel />
      <LeaderBoard />

      <InstallPrompt />
    </div>
  </main>

  <HowToPlay v-if="showHowTo" @close="dismissHowTo" />

  <!--
    Tap the stamp for what the screen actually measures. Three rounds of
    "there is dead space at the bottom" were guesswork because nobody could
    read the numbers off the phone in the room. Now anyone can.
  -->
  <!-- Tap reloads the app (an installed PWA has no reload button); press
       and hold is the viewport measurement, which is a debugging tool and
       not what a tap should do. -->
  <p
    class="build-stamp"
    role="button"
    tabindex="0"
    title="Tap to reload · hold to measure"
    @click="stampTap"
    @pointerdown="stampHold"
    @pointerup="stampRelease"
    @pointerleave="stampRelease"
    @pointercancel="stampRelease"
  >{{ measured || (refreshing ? 'reloading…' : stamp) }}</p>
  <BugReportButton v-if="firebaseConfigured" />
  <BugResolutionNotice v-if="firebaseConfigured" />
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import PlayScreen from '@/components/game/PlayScreen.vue';
import RevealScreen from '@/components/game/RevealScreen.vue';
import RoomLobby from '@/components/lobby/RoomLobby.vue';
import SiteGate from '@/components/auth/SiteGate.vue';
import AdminPanel from '@/components/auth/AdminPanel.vue';
import InstallPrompt from '@/components/shared/InstallPrompt.vue';
import MyStatsPanel from '@/components/shared/MyStatsPanel.vue';
import LeaderBoard from '@/components/shared/LeaderBoard.vue';
import HowToPlay from '@/components/shared/HowToPlay.vue';
import BugReportButton from '@/components/shared/BugReportButton.vue';
import BugResolutionNotice from '@/components/shared/BugResolutionNotice.vue';
import { buildStamp, forceRefresh } from '@/utils/buildStamp';
import { viewportLine } from '@/utils/viewport';
import { initUpdateCheck } from '@/utils/checkForUpdate';

const HOWTO_SEEN_KEY = 'telestrations:howto-seen';
import { PHASES } from '@/engine/game';

export default {
  name: 'App',
  components: {
    PlayScreen, RevealScreen, RoomLobby, SiteGate, AdminPanel, InstallPrompt, BugReportButton, BugResolutionNotice,
    MyStatsPanel, LeaderBoard,
    HowToPlay,
  },
  data() {
    return {
      stamp: buildStamp(), measured: '', refreshing: false, held: false, wantsOnline: false, pendingJoinCode: null, showHowTo: false,
    };
  },
  computed: {
    ...mapState(['roomId', 'roomMeta', 'firebaseConfigured']),
    ...mapGetters(['isApproved', 'isAdmin', 'isRevealing', 'game', 'inLobby']),
    playing() { return this.game?.phase === PHASES.PLAYING; },
  },
  watch: {
    // Lifetime stats. `immediate` matters as much as the watch: a client that
    // mounts straight into the reveal - a refresh, or rejoining a room that
    // already got there - never sees this flip, and that game would never be
    // counted. Recording is idempotent, so firing on every arrival is free.
    isRevealing: {
      handler(revealing) { if (revealing) this.$store.dispatch('recordMyStats'); },
      immediate: true,
    },
    isApproved(ok) { if (ok) this.consumeJoinCode(); },
  },
  created() {
    const code = new URLSearchParams(window.location.search).get('join');
    if (code) { this.pendingJoinCode = code; this.wantsOnline = true; }
  },
  mounted() {
    this.$store.dispatch('initAuth');
    initUpdateCheck(this.$store);
    if (this.isApproved) this.consumeJoinCode();

    // First-ever visit: open the walkthrough unprompted, so a friend arriving
    // from a join link learns the lay of the land before the lobby. Marked
    // seen on DISMISS, not on open. Unlike the sibling games there is no
    // saved-game check - this game has no local play to have saved.
    // The ? button brings it back any time.
    if (!localStorage.getItem(HOWTO_SEEN_KEY)) this.showHowTo = true;
  },
  methods: {
    toggleMeasure() {
      this.measured = this.measured ? '' : viewportLine();
    },
    // Hold for the measurement; a tap is a reload.
    stampHold() {
      this.held = false;
      this.holdTimer = setTimeout(() => { this.held = true; this.toggleMeasure(); }, 550);
    },
    stampRelease() {
      if (this.holdTimer) clearTimeout(this.holdTimer);
      this.holdTimer = null;
    },
    stampTap() {
      if (this.held) { this.held = false; return; }
      if (this.measured) { this.measured = ''; return; }
      this.refreshing = true;
      forceRefresh();
    },
    dismissHowTo() {
      this.showHowTo = false;
      try { localStorage.setItem(HOWTO_SEEN_KEY, '1'); } catch { /* storage off */ }
    },
    consumeJoinCode() {
      if (!this.pendingJoinCode) return;
      const code = this.pendingJoinCode;
      this.pendingJoinCode = null;
      this.$store.dispatch('joinRoomByCode', code);
    },
    leave() { this.$store.dispatch('leaveRoom'); this.wantsOnline = false; },
    confirmLeave() {
      // eslint-disable-next-line no-alert -- one deliberate destructive confirm
      if (window.confirm('Leave this game?')) this.leave();
    },
  },
};
</script>
